import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import IconButton from '../components/IconButton';
import {
  CloseIcon,
  ZeroIcon,
  StartCaptureIcon,
  EndCaptureIcon,
  ExportIcon,
} from '../components/UIIcons';
import { useDeviceOrientation } from '../hooks/useDeviceOrientation';
import { useCamera } from '../hooks/useCamera';
import {
  screenRollDeg,
  gravityInDeviceFrame,
  deltaAngleDeg,
} from '../lib/quaternion';
import {
  TorsionCapture,
  captureFrameWithOverlay,
  buildTorsionReport,
} from '../lib/torsionReport';
import { exportCanvasJpeg, ExportCancelledError } from '../lib/exportImage';
import { haptic } from '../lib/haptics';
import { safeTopPad, safeBottomPad, belowTopBar } from '../lib/safeArea';

/**
 * Live camera goniometer with intraoperative documentation workflow:
 *
 *   Capture Start  →  rotate  →  Capture End  →  Export Report
 *
 * Each capture stores the live camera frame *with the goniometer overlay
 * burned in* (so the documentation image shows what the surgeon saw),
 * plus the angle at that moment. Export builds a side-by-side report image.
 */
export default function TorsionPage() {
  const nav = useNavigate();

  const sensor = useDeviceOrientation();
  const camera = useCamera({ autoStart: false });
  const videoRef = useRef<HTMLVideoElement>(null);

  const [started, setStarted] = useState(false);
  /** Absolute angle defined as 0° by the surgeon. Independent of captures. */
  const [zeroReference, setZeroReference] = useState<number | null>(null);
  const [startCapture, setStartCapture] = useState<TorsionCapture | null>(null);
  const [endCapture, setEndCapture] = useState<TorsionCapture | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  /* Pipe stream into <video>. */
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !camera.stream) return;
    v.srcObject = camera.stream;
    v.play().catch(() => {});
  }, [camera.stream]);

  /* Combined permission + start. Must run from synchronous click. */
  const start = async () => {
    setStarted(true);
    await sensor.request();
    await camera.start();
  };

  /* ─── Live derivations ─── */
  const q = sensor.orientation?.q;
  const live = q ? screenRollDeg(q) : 0;
  const flat =
    q != null && Math.abs(gravityInDeviceFrame(q).gz) > 0.95;

  // The "zeroed" live angle — i.e. delta from the user-defined zero reference
  // (or the absolute angle if no zero is set).
  const liveZeroed =
    zeroReference != null ? deltaAngleDeg(live, zeroReference) : live;

  // What to display prominently in the centre of the goniometer:
  // - if both captures done → frozen final Δ between captures
  // - else → live zeroed angle (delta from zero, or absolute if zero not set)
  const finalDelta =
    startCapture && endCapture
      ? deltaAngleDeg(endCapture.absoluteAngle, startCapture.absoluteAngle)
      : null;
  const display = finalDelta ?? liveZeroed;
  const showingDelta = finalDelta != null || zeroReference != null;

  /* Auto-clear toast. */
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 1600);
    return () => clearTimeout(id);
  }, [toast]);

  /* ─── Actions ─── */
  const setZero = () => {
    if (!q || flat) return;
    setZeroReference(live);
    setToast(`Zero set at ${fmtSigned(live)}°`);
  };

  const clearZero = () => {
    setZeroReference(null);
    setToast('Zero cleared');
  };

  const captureNow = (kind: 'start' | 'end') => {
    const v = videoRef.current;
    if (!v || !q || flat) return;
    void haptic('tap');
    // Burn the zeroed view into the image — that's what the surgeon was
    // looking at on screen, regardless of which capture this is.
    const composite = captureFrameWithOverlay(v, live, zeroReference);
    const displayAngle =
      zeroReference != null ? deltaAngleDeg(live, zeroReference) : live;
    const cap: TorsionCapture = {
      image: composite,
      absoluteAngle: live,
      displayAngleAtCapture: displayAngle,
      timestamp: Date.now(),
    };
    if (kind === 'start') {
      setStartCapture(cap);
      setEndCapture(null); // re-capturing start invalidates end
      setToast(`Start captured · ${fmtSigned(displayAngle)}°`);
    } else {
      setEndCapture(cap);
      const d = deltaAngleDeg(cap.absoluteAngle, startCapture!.absoluteAngle);
      setToast(`End captured · Δ ${fmtSigned(d)}°`);
    }
  };

  const exportReport = async () => {
    if (!startCapture || !endCapture) return;
    try {
      const report = buildTorsionReport(startCapture, endCapture);
      await exportCanvasJpeg(report, `torsion-report-${Date.now()}.jpg`, 0.94);
      void haptic('success');
    } catch (err) {
      if (err instanceof ExportCancelledError) return;
      console.error('[torsion] Export failed', err);
      void haptic('error');
      setToast('Export failed — try again');
    }
  };

  const reset = () => {
    setZeroReference(null);
    setStartCapture(null);
    setEndCapture(null);
    setToast(null);
  };

  /* ─── Render ─── */
  return (
    <div style={pageStyle}>
      <video
        ref={videoRef}
        playsInline
        muted
        autoPlay
        style={videoStyle}
      />
      <div style={vignetteStyle} />

      {q && !flat && (
        <div
          style={{
            ...horizonStyle,
            transform: `translateY(-50%) rotate(${-live}deg)`,
          }}
        />
      )}

      {/* Top bar — safe-area handled via inline style in topBarStyle. */}
      <div style={topBarStyle}>
        <IconButton
          label="Close"
          variant="ghost"
          onClick={() => nav('/app')}
          icon={<CloseIcon />}
        />
        <div style={{ flex: 1 }} />
        <IconButton
          label="Reset"
          variant="ghost"
          onClick={reset}
          disabled={!startCapture && !endCapture}
        />
      </div>

      {/* Centre: goniometer or status messages */}
      {!started ? (
        <Intro onStart={start} />
      ) : sensor.permission === 'denied' ? (
        <PermissionDenied />
      ) : sensor.permission === 'unavailable' ? (
        <Unavailable />
      ) : !q ? (
        <CenterCard title="Waiting for sensors…" body="Move the phone slightly to wake them up." />
      ) : flat ? (
        <CenterCard
          title="Lift the phone"
          body="The phone is nearly horizontal — orientation can't be measured reliably. Hold it more upright."
        />
      ) : (
        <Goniometer
          liveZeroed={liveZeroed}
          display={display}
          isDelta={showingDelta}
          zeroSet={zeroReference != null}
          finalLocked={finalDelta != null}
          startDisplay={startCapture?.displayAngleAtCapture ?? null}
          endDisplay={endCapture?.displayAngleAtCapture ?? null}
          live={live}
          zeroReference={zeroReference}
        />
      )}

      {/* Status pills row — zero, captures, Δ */}
      {started && q && !flat && (
        <div style={statusRowStyle}>
          {zeroReference != null && (
            <button
              type="button"
              onClick={clearZero}
              style={{ ...zeroChipStyle, cursor: 'pointer' }}
              aria-label="Clear zero reference"
            >
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ff9f0a' }} />
              Zero · {fmtSigned(zeroReference)}°
              <CloseIcon width={12} height={12} style={{ opacity: 0.5, marginLeft: 4 }} />
            </button>
          )}
          <CaptureChip label="Start" capture={startCapture} />
          <CaptureChip label="End" capture={endCapture} />
          {finalDelta != null && (
            <div style={deltaChipStyle}>
              Δ {fmtSigned(finalDelta)}°
            </div>
          )}
        </div>
      )}

      {/* Toast — appears briefly over the status pills row */}
      {toast && <div style={toastStyle}>{toast}</div>}

      {/* Bottom action bar (buttons only — no other layers underneath) */}
      {started && q && !flat && (
        <div style={bottomBarStyle}>
          <IconButton
            label={zeroReference != null ? 'Re-zero' : 'Set Zero'}
            variant="default"
            size="md"
            onClick={setZero}
            icon={<ZeroIcon />}
          />
          <IconButton
            label={startCapture ? 'Re-Start' : 'Start'}
            variant={!startCapture ? 'primary' : 'default'}
            size={!startCapture ? 'lg' : 'md'}
            onClick={() => captureNow('start')}
            icon={<StartCaptureIcon />}
          />
          <IconButton
            label={endCapture ? 'Re-End' : 'End'}
            variant={startCapture && !endCapture ? 'primary' : 'default'}
            size={startCapture && !endCapture ? 'lg' : 'md'}
            disabled={!startCapture}
            onClick={() => captureNow('end')}
            icon={<EndCaptureIcon />}
          />
          <IconButton
            label="Export"
            variant={startCapture && endCapture ? 'primary' : 'default'}
            size={startCapture && endCapture ? 'lg' : 'md'}
            disabled={!startCapture || !endCapture}
            onClick={exportReport}
            icon={<ExportIcon />}
          />
        </div>
      )}

    </div>
  );
}

/* ─────────────────────── Subcomponents ─────────────────────── */

function Intro({ onStart }: { onStart: () => void }) {
  return (
    <div style={centerColStyle}>
      <div style={{ fontSize: 22, fontWeight: 700 }}>Torsion Documentation</div>
      <div
        style={{
          color: 'var(--text-dim)',
          maxWidth: 320,
          textAlign: 'center',
          fontSize: 14,
          lineHeight: 1.4,
        }}
      >
        Align the phone with your reference, capture the start position,
        rotate, capture the end. Export a side-by-side report.
      </div>
      <IconButton label="Start" variant="primary" size="lg" onClick={onStart} />
    </div>
  );
}

function PermissionDenied() {
  return (
    <div style={centerColStyle}>
      <div style={{ fontSize: 18, fontWeight: 600 }}>Motion permission denied</div>
      <div style={{ color: 'var(--text-dim)', maxWidth: 320, textAlign: 'center', fontSize: 14 }}>
        In iOS Settings → Safari → Motion &amp; Orientation Access, enable the
        permission and reload.
      </div>
      <IconButton label="Reload" variant="primary" onClick={() => location.reload()} />
    </div>
  );
}

function Unavailable() {
  return (
    <div style={centerColStyle}>
      <div style={{ fontSize: 18, fontWeight: 600 }}>Sensors unavailable</div>
      <div style={{ color: 'var(--text-dim)', maxWidth: 320, textAlign: 'center', fontSize: 14 }}>
        This device or browser does not expose orientation sensors. Try iPhone
        Safari over HTTPS.
      </div>
    </div>
  );
}

function CenterCard({ title, body }: { title: string; body: string }) {
  return (
    <div style={centerColStyle}>
      <div style={{ fontSize: 18, fontWeight: 600 }}>{title}</div>
      <div style={{ color: 'var(--text-dim)', maxWidth: 320, textAlign: 'center', fontSize: 14 }}>{body}</div>
    </div>
  );
}

function Goniometer({
  liveZeroed,
  display,
  isDelta,
  zeroSet,
  finalLocked,
  startDisplay,
  endDisplay,
  live,
  zeroReference,
}: {
  /** Live angle relative to zero (or absolute if zero not set). */
  liveZeroed: number;
  /** What to show prominently in the centre — final Δ or live zeroed. */
  display: number;
  /** True when the centre value is showing a delta rather than absolute. */
  isDelta: boolean;
  /** Whether the user has set a zero reference. */
  zeroSet: boolean;
  /** Whether the final captures-to-captures Δ is frozen and being shown. */
  finalLocked: boolean;
  /** Display angle of the Start capture, in zeroed coords (matches the burn-in). */
  startDisplay: number | null;
  /** Display angle of the End capture, in zeroed coords. */
  endDisplay: number | null;
  /** Raw absolute live angle. */
  live: number;
  /** The absolute angle the user designated as zero, if any. */
  zeroReference: number | null;
}) {
  const size = 280;
  const r = size / 2;
  const ringR = r - 16;
  const ticks: { deg: number; major: boolean }[] = [];
  for (let d = 0; d < 360; d += 5) ticks.push({ deg: d, major: d % 30 === 0 });

  return (
    <div style={ringContainerStyle}>
      <div style={{ width: size, height: size, position: 'relative' }}>
        <svg
          viewBox={`-${r} -${r} ${size} ${size}`}
          width={size}
          height={size}
          style={{ position: 'absolute', inset: 0 }}
        >
          <circle
            cx={0}
            cy={0}
            r={ringR}
            fill="rgba(0,0,0,0.32)"
            stroke="rgba(255,255,255,0.32)"
            strokeWidth={1}
          />

          {ticks.map(({ deg, major }) => {
            const a = ((deg - 90) * Math.PI) / 180;
            const inner = major ? ringR - 12 : ringR - 6;
            return (
              <line
                key={deg}
                x1={Math.cos(a) * inner}
                y1={Math.sin(a) * inner}
                x2={Math.cos(a) * ringR}
                y2={Math.sin(a) * ringR}
                stroke={major ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.45)'}
                strokeWidth={major ? 1.5 : 1}
                strokeLinecap="round"
              />
            );
          })}

          {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((d) => {
            const a = ((d - 90) * Math.PI) / 180;
            const lr = ringR - 26;
            const lbl = d > 180 ? d - 360 : d;
            return (
              <text
                key={`l${d}`}
                x={Math.cos(a) * lr}
                y={Math.sin(a) * lr}
                fill="rgba(255,255,255,0.55)"
                fontSize={d % 90 === 0 ? 11 : 9}
                textAnchor="middle"
                dominantBaseline="middle"
                fontFamily="-apple-system, system-ui, sans-serif"
              >
                {Math.abs(lbl)}
              </text>
            );
          })}

          {/* Zero-reference marker — orange dot fixed at the top of the ring
              (0°) when the user has set zero. The pointer in delta mode is
              expressed relative to this. */}
          {zeroSet && (
            <Marker angle={0} colour="#ff9f0a" radius={ringR - 14} size={10} />
          )}

          {/* Start-capture marker — small light-cyan dot at the captured
              displayed angle on the dial */}
          {startDisplay != null && (
            <Marker
              angle={startDisplay}
              colour="#a8d8ff"
              radius={ringR - 14}
              size={9}
            />
          )}

          {/* End-capture marker — green dot */}
          {endDisplay != null && (
            <Marker
              angle={endDisplay}
              colour="#30d158"
              radius={ringR - 14}
              size={11}
            />
          )}

          {/* Live pointer — bright cyan, always at the current zeroed angle.
              When the final Δ is locked, point at the end position so the
              dial visualises the correction vs. start. */}
          <Marker
            angle={finalLocked ? endDisplay! : liveZeroed}
            colour="#7cc4ff"
            radius={ringR - 14}
            size={12}
            withLine
          />
        </svg>

        <div style={ringTextStyle}>
          {isDelta && (
            <div
              style={{
                fontSize: 11,
                color: 'var(--text-dim)',
                letterSpacing: 0.6,
                textTransform: 'uppercase',
              }}
            >
              {finalLocked ? 'Δ corrected' : zeroSet ? 'Δ from zero' : ''}
            </div>
          )}
          <div
            style={{
              fontSize: 56,
              fontWeight: 700,
              letterSpacing: -1.5,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1,
              marginTop: 4,
            }}
          >
            {fmtSigned(display)}°
          </div>
          <div
            style={{
              marginTop: 6,
              fontSize: 12,
              color: 'var(--text-dim)',
              fontVariantNumeric: 'tabular-nums',
              minHeight: 16,
            }}
          >
            {finalLocked && startDisplay != null && endDisplay != null
              ? `start ${fmtSigned(startDisplay)}° · end ${fmtSigned(endDisplay)}°`
              : zeroSet
                ? `live ${fmtSigned(live)}° · zero ${fmtSigned(zeroReference!)}°`
                : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function Marker({
  angle,
  colour,
  radius,
  size,
  withLine = false,
}: {
  angle: number;
  colour: string;
  radius: number;
  size: number;
  withLine?: boolean;
}) {
  const a = ((angle - 90) * Math.PI) / 180;
  const cx = Math.cos(a) * radius;
  const cy = Math.sin(a) * radius;
  return (
    <g>
      {withLine && (
        <line
          x1={0}
          y1={0}
          x2={cx}
          y2={cy}
          stroke={colour}
          strokeWidth={2}
          strokeLinecap="round"
          opacity={0.7}
        />
      )}
      <circle cx={cx} cy={cy} r={size / 2 + 1} fill="rgba(0,0,0,0.5)" />
      <circle cx={cx} cy={cy} r={size / 2} fill={colour} />
    </g>
  );
}

function CaptureChip({
  label,
  capture,
}: {
  label: string;
  capture: TorsionCapture | null;
}) {
  const captured = capture != null;
  const colour = '#7cc4ff';
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        borderRadius: 999,
        background: captured ? 'rgba(124,196,255,0.14)' : 'rgba(255,255,255,0.06)',
        border: `1px solid ${captured ? colour : 'var(--hairline)'}`,
        fontSize: 12,
        fontWeight: 600,
        color: captured ? colour : 'var(--text-dim)',
        fontVariantNumeric: 'tabular-nums',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: captured ? colour : 'var(--text-dim)' }} />
      {label}
      {captured && <span>· {fmtSigned(capture!.displayAngleAtCapture)}°</span>}
    </div>
  );
}

/* ─────────────────────── Helpers + styles ─────────────────────── */

function fmtSigned(d: number): string {
  const sign = d > 0 ? '+' : d < 0 ? '−' : '';
  return `${sign}${Math.abs(d).toFixed(1)}`;
}

const pageStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: '#000', overflow: 'hidden',
};
const videoStyle: React.CSSProperties = {
  position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
};
const vignetteStyle: React.CSSProperties = {
  position: 'absolute', inset: 0,
  background: 'radial-gradient(ellipse at center, rgba(0,0,0,0) 50%, rgba(0,0,0,0.5) 100%)',
  pointerEvents: 'none',
};
const horizonStyle: React.CSSProperties = {
  position: 'absolute', top: '50%', left: '-25%', width: '150%', height: 1,
  background:
    'linear-gradient(to right, rgba(124,196,255,0) 0%, rgba(124,196,255,0.7) 25%, rgba(124,196,255,0.7) 75%, rgba(124,196,255,0) 100%)',
  pointerEvents: 'none', transformOrigin: 'center', willChange: 'transform',
};
const topBarStyle: React.CSSProperties = {
  position: 'absolute', top: 0, left: 0, right: 0,
  paddingTop: safeTopPad(12),
  paddingBottom: 12,
  paddingLeft: 16,
  paddingRight: 16,
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  gap: 8,
  background: 'linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0))',
  zIndex: 2,
};
const ringContainerStyle: React.CSSProperties = {
  position: 'absolute', inset: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  pointerEvents: 'none',
};
const ringTextStyle: React.CSSProperties = {
  position: 'absolute', inset: 0,
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  color: '#fff',
};
const statusRowStyle: React.CSSProperties = {
  position: 'absolute',
  // Just below the top bar — far away from the action buttons.
  // belowTopBar keeps the offset in sync with the actual top-bar
  // height including the safe-area inset (see lib/safeArea.ts).
  top: belowTopBar(64, 14),
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: 8,
  flexWrap: 'wrap',
  justifyContent: 'center',
  maxWidth: 'calc(100vw - 32px)',
  zIndex: 1,
};
const deltaChipStyle: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: 999,
  background: 'rgba(48,209,88,0.18)',
  border: '1px solid rgba(48,209,88,0.5)',
  color: 'var(--good)',
  fontSize: 13,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
};
const zeroChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 12px',
  borderRadius: 999,
  background: 'rgba(255,159,10,0.14)',
  border: '1px solid rgba(255,159,10,0.5)',
  color: '#ff9f0a',
  fontSize: 12,
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
};
const bottomBarStyle: React.CSSProperties = {
  position: 'absolute', left: 0, right: 0, bottom: 0,
  paddingTop: 12,
  paddingBottom: safeBottomPad(16),
  paddingLeft: 16,
  paddingRight: 16,
  display: 'flex', justifyContent: 'center', alignItems: 'center',
  gap: 8, flexWrap: 'wrap',
  background: 'linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0))',
  zIndex: 2,
};
const centerColStyle: React.CSSProperties = {
  position: 'absolute', inset: 0,
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  gap: 14, padding: 24, textAlign: 'center', pointerEvents: 'auto',
};
const toastStyle: React.CSSProperties = {
  position: 'absolute',
  top: belowTopBar(64, 14),
  left: '50%', transform: 'translateX(-50%)',
  background: 'rgba(48, 209, 88, 0.18)',
  border: '1px solid rgba(48, 209, 88, 0.5)',
  color: 'var(--good)',
  padding: '10px 16px', borderRadius: 999,
  fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
  zIndex: 3,
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
};
